import React, { useRef } from "react";
import { observer } from "mobx-react";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { NavigationContainer, NavigationState } from "@react-navigation/native";
import { LoginStackScreen } from "./LoginStackScreen";
import { mobUser } from "../mobx/MobUser";
import LoggedInTabNavigator from "./LoggedInTabNavigator";
import Chat from "../pages/Home/Chat";
import Queries from "../api/queries";
import { mobAlert } from "../mobx/MobAlert";
import i18n from "../i18n";
import { mobNotif } from "../mobx/MobNotifs";
import { useTheme } from "react-native-paper";
import { isToday, isFuture } from "date-fns";

const Stack = createNativeStackNavigator();

export type RootStackParamList = {
  RootStack: undefined;
  Chat: undefined;
  CheckIn: undefined;
  CheckOut: undefined;
  // Ajoute ici tes autres routes si besoin
};

const Navigation = observer(() => {
  const theme = useTheme();
  const queries = new Queries();
  const navigationRef = useRef<any>(null);

  const checkInAlert = async () => {
    try {
      const resa = await queries.getResaInfo(mobUser.OiqiaBookingId);
      if (!resa) return;

      const containsStatus10 = resa.bookingStep.some((e) => e.status === 10);
      const containsStatus20 = resa.bookingStep.some((e) => e.status === 20);

      if (containsStatus10 && !containsStatus20) {
        mobAlert.setAlert(
          i18n.t("alert.CHECKIN_TITLE"),
          i18n.t("alert.CHECKIN_DESCRIPTION"),
          i18n.t("alert.CHECKIN_BUTTON"),
          () => {
            navigationRef.current?.navigate("CheckIn");
            mobAlert.closeAlert();
          }
        );
      }
    } catch (error) {
      // Optionnel: log error
    }
  };

  const checkOutAlert = async () => {
    try {
      const resa = await queries.getResaInfo(mobUser.OiqiaBookingId);
      if (!resa) return;

      const containsStatus20 = resa.bookingStep.some((e) => e.status === 20);
      const containsStatus30 = resa.bookingStep.some((e) => e.status === 30);

      if (
        resa.departure &&
        (isToday(new Date(resa.departure)) || isFuture(new Date(resa.departure))) &&
        containsStatus20 &&
        !containsStatus30
      ) {
        mobAlert.setAlert(
          i18n.t("alert.CHECKOUT_TITLE"),
          i18n.t("alert.CHECKOUT_DESCRIPTION"),
          i18n.t("alert.CHECKOUT_BUTTON"),
          () => {
            navigationRef.current?.navigate("CheckOut");
            mobAlert.closeAlert();
          }
        );
      }
    } catch (error) {
      // Optionnel: log error
    }
  };

  const getCurrentRouteName = (state: NavigationState | undefined): string | null => {
    if (!state || !state.routes) return null;
    const route = state.routes[state.index];
    if (route.state) {
      return getCurrentRouteName(route.state);
    }
    return route.name ?? null;
  };

  const onStateChange = async (state: NavigationState | undefined) => {
    if (!mobUser.UserLogged) return;

    const currentRouteName = getCurrentRouteName(state) ?? "";
    if (!["CheckIn"].includes(currentRouteName)) {
      await checkInAlert();
    }
    // Si tu souhaites réactiver checkOutAlert, décommente ci-dessous:
    /*
    if (!["CheckOut"].includes(currentRouteName)) {
      await checkOutAlert();
    }
    */
    if (currentRouteName === "Main") {
      mobNotif.subscribeToUnreadNumber();
    }
  };

  return (
    <NavigationContainer
      ref={navigationRef}
      onStateChange={onStateChange}
      theme={{
        colors: {
          background: theme.colors.background,
          primary: theme.colors.primary,
          card: theme.colors.background,
          text: theme.colors.onBackground,
          border: "transparent",
          notification: theme.colors.error,
        },
        dark: false,
      }}
    >
      <Stack.Navigator
        screenOptions={{
          statusBarColor: theme.colors.primary,
          navigationBarHidden: false,
          contentStyle: {
            backgroundColor: theme.colors.background,
          },
          headerShown: false,
        }}
      >
        {mobUser.UserLogged ? (
          <>
            <Stack.Screen name="RootStack" component={LoggedInTabNavigator} />
            <Stack.Screen name="Chat" component={Chat} />
          </>
        ) : (
          <Stack.Screen name="LoginStack" component={LoginStackScreen} />
        )}
      </Stack.Navigator>
    </NavigationContainer>
  );
});

export default Navigation;
